// The things this site writes about, as schema.org entities, so search engines and AI
// answer engines can tie each page to the tick, the illness or the place it is about.
//
// Each Wikipedia URL was checked on 2026-10-13 to load its own article (its canonical
// link is itself, not a redirect). entities.test.ts fails when a disease or species is
// added without one.

/** Wikipedia article for each disease page, by slug. */
export const DISEASE_SAME_AS: Readonly<Record<string, string>> = {
  "alpha-gal-syndrome": "https://en.wikipedia.org/wiki/Alpha-gal_syndrome",
  "lyme-disease": "https://en.wikipedia.org/wiki/Lyme_disease",
  ehrlichiosis: "https://en.wikipedia.org/wiki/Ehrlichiosis",
  // The plain "Anaplasmosis" article is mostly about livestock; this one is the human illness.
  anaplasmosis: "https://en.wikipedia.org/wiki/Human_granulocytic_anaplasmosis",
  "rocky-mountain-spotted-fever": "https://en.wikipedia.org/wiki/Rocky_Mountain_spotted_fever",
  babesiosis: "https://en.wikipedia.org/wiki/Babesiosis",
  tularemia: "https://en.wikipedia.org/wiki/Tularemia",
  stari: "https://en.wikipedia.org/wiki/Southern_tick-associated_rash_illness",
  "heartland-virus": "https://en.wikipedia.org/wiki/Heartland_virus",
  "bourbon-virus": "https://en.wikipedia.org/wiki/Bourbon_virus",
  "powassan-virus": "https://en.wikipedia.org/wiki/Powassan_virus",
};

/** Wikipedia article for each tick species page, by slug. */
export const SPECIES_SAME_AS: Readonly<Record<string, string>> = {
  "lone-star-tick": "https://en.wikipedia.org/wiki/Amblyomma_americanum",
  "blacklegged-tick": "https://en.wikipedia.org/wiki/Ixodes_scapularis",
  "western-blacklegged-tick": "https://en.wikipedia.org/wiki/Ixodes_pacificus",
  "american-dog-tick": "https://en.wikipedia.org/wiki/Dermacentor_variabilis",
  "brown-dog-tick": "https://en.wikipedia.org/wiki/Rhipicephalus_sanguineus",
  "gulf-coast-tick": "https://en.wikipedia.org/wiki/Amblyomma_maculatum",
  "rocky-mountain-wood-tick": "https://en.wikipedia.org/wiki/Dermacentor_andersoni",
  "asian-longhorned-tick": "https://en.wikipedia.org/wiki/Haemaphysalis_longicornis",
};

export type Entity = Record<string, unknown>;

export function conditionEntity(c: {
  name: string;
  alternateName?: string[];
  sameAs?: string;
}): Entity {
  return {
    "@type": "MedicalCondition",
    name: c.name,
    ...(c.alternateName?.length ? { alternateName: c.alternateName } : {}),
    ...(c.sameAs ? { sameAs: c.sameAs } : {}),
  };
}

export function taxonEntity(t: { name: string; scientificName: string; sameAs?: string }): Entity {
  return {
    "@type": "Taxon",
    name: t.name,
    alternateName: t.scientificName,
    taxonRank: "species",
    ...(t.sameAs ? { sameAs: t.sameAs } : {}),
  };
}

/** A state, or a county inside one. */
export function placeEntity(name: string, containedIn?: string): Entity {
  return {
    "@type": "AdministrativeArea",
    name,
    ...(containedIn
      ? { containedInPlace: { "@type": "AdministrativeArea", name: containedIn } }
      : { containedInPlace: { "@type": "Country", name: "United States" } }),
  };
}

export const LONE_STAR_TICK = taxonEntity({
  name: "Lone Star tick",
  scientificName: "Amblyomma americanum",
  sameAs: SPECIES_SAME_AS["lone-star-tick"],
});

export const ALPHA_GAL_SYNDROME = conditionEntity({
  name: "Alpha-gal syndrome",
  alternateName: ["AGS", "Alpha-gal allergy", "Mammalian meat allergy"],
  sameAs: DISEASE_SAME_AS["alpha-gal-syndrome"],
});
